// PHA-1168: the Billing Report when a saved billing view is selected. The codes
// come from the same billing engine as the standard report; the view only picks,
// filters, sorts and groups rows. A view that filters rows says how many billable
// rows it hides, every time.
import React, { useEffect, useState } from 'react';
import { Badge, Button, Callout, Card, Flex, Heading, Text } from '@radix-ui/themes';
import { DownloadIcon, ExclamationTriangleIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { customViewErrorCode, customViewsApi, CustomViewResult } from '../../services/api';
import ManifestTable from './ManifestTable';
import { downloadCsv, safeFileName } from './format';

const PAGE = 200;

interface Props {
  orgId: string;
  viewId: number;
  viewName: string;
  fromDate: string;
  toDate: string;
  /** Bumped by "Generate Report"; the view loads only on an explicit generate. */
  generation: number;
  onUnavailable: (reason: 'not_found' | 'stale') => void;
}

const CustomBillingView: React.FC<Props> = ({ orgId, viewId, viewName, fromDate, toDate, generation, onUnavailable }) => {
  const [result, setResult] = useState<CustomViewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setShown(PAGE);
    customViewsApi
      .render(orgId, viewId, { from_date: fromDate, to_date: toDate })
      .then((data) => {
        if (cancelled) return;
        if (data.stale) { onUnavailable('stale'); return; }
        setResult(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (customViewErrorCode(err) === 'view_not_found') { onUnavailable('not_found'); return; }
        setError(err instanceof Error ? err.message : 'Could not load this view.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Re-run only on an explicit generate (and when the view itself changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, viewId, generation]);

  if (loading && !result) {
    return (
      <Flex justify="center" p="6">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
      </Flex>
    );
  }
  if (error) {
    return (
      <Callout.Root color="orange">
        <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
        <Callout.Text>{error}</Callout.Text>
      </Callout.Root>
    );
  }
  if (!result) return null;

  const meta = result.manifest.billing || {};
  const hidden = result.hidden_billable_rows || 0;
  const records = result.records.slice(0, shown);

  return (
    <Flex direction="column" gap="3" data-testid="custom-billing-view">
      <Flex justify="between" align="start" gap="3" wrap="wrap">
        <Flex direction="column" gap="1">
          <Heading size="4">{meta.title || viewName}</Heading>
          {meta.description && <Text size="2" color="gray">{meta.description}</Text>}
          <Flex gap="2" align="center">
            <Badge variant="soft" color="gray">{result.total_count} rows</Badge>
            <Text size="1" color="gray">{fromDate} to {toDate}</Text>
            {loading && <Text size="1" color="gray">Updating…</Text>}
          </Flex>
        </Flex>
        <Button
          variant="soft"
          disabled={!result.csv}
          onClick={() => result.csv && downloadCsv(result.csv, `${safeFileName(meta.title || viewName)}_${fromDate}_to_${toDate}.csv`)}
        >
          <DownloadIcon /> Export CSV
        </Button>
      </Flex>

      {hidden > 0 && (
        <Callout.Root color="amber" data-testid="hidden-billable-banner">
          <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
          <Callout.Text>
            This view hides <strong>{hidden}</strong> billable {hidden === 1 ? 'row' : 'rows'} of{' '}
            {result.billable_total}. Switch to Phanova Standard to see every billable patient.
          </Callout.Text>
        </Callout.Root>
      )}
      {result.notice && (
        <Callout.Root color="blue">
          <Callout.Icon><InfoCircledIcon /></Callout.Icon>
          <Callout.Text>{result.notice}</Callout.Text>
        </Callout.Root>
      )}

      <Card style={{ overflowX: 'auto' }}>
        <ManifestTable
          manifest={result.manifest}
          records={records}
          groups={shown >= result.records.length ? result.groups : undefined}
          totals={shown >= result.records.length ? result.totals : undefined}
          emptyText="No rows match this view for the selected dates."
        />
      </Card>
      {result.records.length > shown && (
        <Flex justify="center">
          <Button variant="soft" onClick={() => setShown((n) => n + PAGE)}>
            Show more ({result.records.length - shown} more)
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default CustomBillingView;
